import {
  useBusinessUserQuery,
  useChangeBusinessUserPasswordRequest,
  useChangePasswordRequest,
  useDeleteBusinessUserMutation,
  useMeQuery,
  useMyTariffQuery,
} from '@streamster/shared/api/api.portal';
import { Me } from '@streamster/shared/api/types';
import { BusinessUserSwitcher } from '#/components/BusinessUserSwitcher/BusinessUserSwitcher';
import Heading from '#/components/Heading/Heading';
import Loader from '@streamster/shared/components/Loader/Loader';
import PageWrapper from '#/components/PageWrapper/PageWrapper';
import { AuthContext } from '@streamster/shared/screens/auth/auth.utils';
import { useSelectedBusinessUserStore } from '@streamster/shared/screens/home/selectedBusinessUser.store';
import { ManagerSettingsSection } from '#/screens/profile/ManagerSettingsSection';
import { ProfileBox } from '#/screens/profile/Profile';
import { ProfileCompleteBox } from '#/screens/profile/ProfileCompleteBox';
import { ToasterContext } from '@streamster/shared/toaster/toaster.provider';
import { useContext } from 'react';

export const ProfileScreen = () => {
  const { data: profile } = useMeQuery();
  const { selectedUserId } = useSelectedBusinessUserStore();

  if (!profile) {
    return <Loader />;
  }

  const isBusiness = profile.business;
  const isMyUserSelected = selectedUserId === profile.userId || !selectedUserId;

  return (
    <PageWrapper>
      {selectedUserId ? <BusinessUserSwitcher /> : <Heading variant="page">Profile</Heading>}
      {isBusiness && !isMyUserSelected ? (
        <ProfileBusinessContainer selectedUserId={selectedUserId} />
      ) : (
        <ProfileContainer profile={profile} />
      )}
    </PageWrapper>
  );
};

const ProfileBusinessContainer = ({ selectedUserId }: { selectedUserId: number }) => {
  const { data: user } = useBusinessUserQuery(selectedUserId);
  const [changePassword] = useChangeBusinessUserPasswordRequest();
  const deleteMutation = useDeleteBusinessUserMutation();
  const { error, success } = useContext(ToasterContext);
  const { selectUser } = useSelectedBusinessUserStore();

  if (!user) {
    return <Loader />;
  }

  const handleChangePassword = (password: string) => {
    changePassword(selectedUserId, password)
      .then(() => success({ message: 'Password has been changed' }))
      .catch(err => error({ title: 'Password is invalid', message: err.message }));
  };

  const handleDeleteAccount = () => {
    deleteMutation.mutateAsync(selectedUserId).then(() => {
      selectUser(null);
    });
  };

  return (
    <>
      <ProfileBox
        user={user}
        tariff={{ name: user.tariffName || '', tariffExpiresAt: user.tariffExpiresAt }}
        onChangePassword={handleChangePassword}
        onDeleteAccount={handleDeleteAccount}
      />
      {/* Manager rights + assigned streamers live here, on the selected manager's profile. */}
      {user.role === 'manager' && <ManagerSettingsSection managerId={selectedUserId} />}
    </>
  );
};

const ProfileContainer = ({ profile }: { profile: Me }) => {
  const { logout, deleteAccount } = useContext(AuthContext);
  const { error, success } = useContext(ToasterContext);
  const [changePassword] = useChangePasswordRequest();
  const { data: myTariff } = useMyTariffQuery();

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      await logout();
      success({ message: 'You have been logged out', title: 'Your account has been deleted' });
    } catch (err) {
      error({ message: 'Please try again' });
    }
  };

  const handleChangePassword = async (newPassword: string) => {
    try {
      await changePassword(newPassword);

      success({
        title: 'Please log in to access your account',
        message: 'Your password was changed',
      });
    } catch (err: any) {
      error({ title: 'Password is invalid', message: err.message });
    }
  };

  if (!myTariff) {
    return <Loader />;
  }

  if (!profile.complete) {
    return <ProfileCompleteBox profile={profile} />;
  }

  return (
    <ProfileBox
      user={profile}
      tariff={myTariff}
      showUpgradeToBusiness={!profile.business}
      onChangePassword={handleChangePassword}
      onDeleteAccount={handleDeleteAccount}
      userIsBusiness={profile.business}
      showDeleteAccountButton
    />
  );
};
